import React, { useState, useRef, useEffect } from 'react'
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    makeStyles,
    Theme,
    useTheme,
    FormControl,
    Select,
    Switch,
    Typography,
    MenuItem,
    Button,
    Tooltip,
    Menu,
} from '@material-ui/core'
import AssessmentIcon from '@material-ui/icons/Assessment'
import TableChartIcon from '@material-ui/icons/TableChart'
import CheckIcon from '@material-ui/icons/Check'
import { ToggleButton, ToggleButtonGroup, Pagination } from '@material-ui/lab'
import {
    StartDateDESC,
    renderCellContent,
    TableHeaders,
    getSortOptionLabels,
    getActiveSortLabel,
    TableHeader,
} from '../OrganizationFacilities/Projects/ProjectTableCell'
import { Container } from '../../components'
import {
    useVendorProjectAnalytics,
    useVendorProfitTrends,
    VendorProjectAnalytics,
    ProjectAnalytics,
} from '../../hooks/useEZFolioProject'
import { useQuery } from '@tanstack/react-query'
import { useUser } from '../../hooks'
import { axiosInstance } from '../../helpers'
import { toast } from 'react-toastify'
import {
    useVendorInvoice,
    INVOICE_CODE_STEP,
} from '../../contexts/useVendorInvoice'
import { VendorInvoiceCodeGate } from '../Invoice/VendorInvoiceCodeGate'
import { ProjectAnalyticsCharts } from '../OrganizationFacilities/Projects/ProjectAnalyticsCharts'
import { VendorProfitCharts } from './VendorProfitCharts'

const EXCLUDED_FIELDS = new Set([
    'concern_level',
    'baseline_budget',
    'budget_variance',
    'actions',
    'unassigned_units',
])

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        tableLayout: 'fixed',
        minWidth: '1200px',
        '& th:first-child, & td:first-child': {
            width: '200px',
        },
        '& th, & td': {
            width: '90px',
        },
        '& th:last-child, & td:last-child': {
            width: '90px',
        },
    },
    tooltip: {
        fontSize: '14px',
        fontFamily: theme.typography.fontFamily,
    },
    tableHeader: {
        '& th': {
            position: 'sticky',
            zIndex: 2,
        },
    },
    headerTitleRow: {
        '& th': {
            top: 0,
            zIndex: 3,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontWeight: 600,
            fontSize: '0.875rem',
            textAlign: 'center',
            verticalAlign: 'bottom',
            padding: theme.spacing(1, 0.5),
            whiteSpace: 'normal',
            lineHeight: 1.25,
            wordBreak: 'break-word',
            borderBottom: 'none',
        },
    },
    headerSortToolbarRow: {
        '& th': {
            top: 'var(--analytics-title-row-height, 56px)',
            height: 36,
            zIndex: 2,
            padding: theme.spacing(0.25, 0.5),
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.text.primary,
            textAlign: 'center',
            verticalAlign: 'middle',
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 1px 0 rgba(0,0,0,0.06)',
        },
    },
    sortButton: {
        color: theme.palette.text.secondary,
        fontSize: '0.7rem',
        lineHeight: 1.2,
        padding: theme.spacing(0.25, 0.5),
        textTransform: 'none',
        maxWidth: '100%',
        minWidth: 0,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
        },
    },
    sortButtonActive: {
        color: theme.palette.primary.main,
        fontWeight: 600,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.primary.dark,
        },
    },
    sortButtonLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        width: '100%',
    },
    cell: {
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'center',
        fontSize: '0.875rem',
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    customSwitch: {
        '& .MuiSwitch-switchBase': {
            color: theme.palette.success.main,
        },
        '& .MuiSwitch-track': {
            backgroundColor: theme.palette.success.main,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
            color: theme.palette.error.main,
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: theme.palette.error.main,
        },
    },
}))

export const VendorProjectAnalyticsTable: React.FC = () => {
    const classes = useStyles()
    const theme = useTheme()

    const { workspaceUser, organization } = useUser()
    const vendorInvoiceController = useVendorInvoice()

    const vendorId = workspaceUser?.active_workspace.id ?? 0

    const [orderBy, setOrderBy] = useState<string>(StartDateDESC)
    const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(
        null,
    )
    const [sortMenuHeader, setSortMenuHeader] = useState<TableHeader | null>(
        null,
    )
    const sortMenuOpen = Boolean(sortMenuAnchor)
    const titleRowRef = useRef<HTMLTableRowElement>(null)
    const [titleRowHeight, setTitleRowHeight] = useState(56)
    const [showArchived, setShowArchived] = useState(false)
    const [viewMode, setViewMode] = useState<'serviceOrders' | 'profit'>(
        'serviceOrders',
    )
    const budgetUnlocked = vendorInvoiceController.step !== INVOICE_CODE_STEP

    const [showChartView, setShowChartView] = useState(false)
    const [trendsStartDate, setTrendsStartDate] = useState('')
    const [trendsEndDate, setTrendsEndDate] = useState('')

    const {
        projectAnalytics,
        projectAnalyticsLoading,
        totalCount,
        numPages,
        currentPage,
        start,
        end,
        pageSize,
        goToPage,
        changePageSize,
    } = useVendorProjectAnalytics({
        vendorId,
        limit: 10,
        offset: 0,
        sort: orderBy,
        archived: showArchived,
        organizationId: organization ?? undefined,
    })

    const {
        data: allProjectAnalytics,
        isLoading: allProjectsLoading,
    } = useQuery<{
        data: { results: VendorProjectAnalytics[]; count: number }
    }>({
        queryKey: [
            'vendor-project-analytics-all',
            vendorId,
            showArchived,
            organization,
        ],
        queryFn: () =>
            axiosInstance.get(
                `/company/vendor/${vendorId}/project-analytics/`,
                {
                    params: {
                        limit: 10000,
                        offset: 0,
                        sort: StartDateDESC,
                        ...(showArchived && { archived: showArchived }),
                        ...(organization && {
                            organization_id: organization,
                        }),
                    },
                },
            ),
        enabled: !!vendorId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })

    const allProjectsForCharts = ((allProjectAnalytics?.data.results ??
        []) as unknown) as ProjectAnalytics[]

    const profitChartEnabled =
        showChartView && viewMode === 'profit' && budgetUnlocked

    const { trends, trendsLoading } = useVendorProfitTrends({
        vendorId,
        archived: showArchived,
        organizationId: organization ?? undefined,
        startDate: trendsStartDate || undefined,
        endDate: trendsEndDate || undefined,
        enabled: !!vendorId && profitChartEnabled,
    })

    const renameCostToPrice = (text: string) =>
        text.replace(/\bCost\b/g, 'Price').replace(/\bcost\b/g, 'price')

    const tableViewMode = viewMode === 'profit' ? 'budget' : viewMode

    const filteredTableHeaders = TableHeaders.filter(
        (header) =>
            !EXCLUDED_FIELDS.has(header.field) &&
            (header.viewMode === 'all' || header.viewMode === tableViewMode),
    ).map((header) => {
        let display = renameCostToPrice(header.display)
        let tooltip = renameCostToPrice(header.tooltip)
        if (header.field === 'units_scheduled') {
            display = 'Units Scheduled'
            tooltip = 'Total number of units scheduled'
        }
        return { ...header, display, tooltip }
    })

    useEffect(() => {
        const row = titleRowRef.current
        if (!row) return

        const measure = () => {
            setTitleRowHeight(row.getBoundingClientRect().height)
        }

        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(row)
        return () => observer.disconnect()
    }, [filteredTableHeaders, viewMode])

    const [
        selectedProject,
        setSelectedProject,
    ] = useState<VendorProjectAnalytics | null>(null)

    const [serviceLevelData, setServiceLevelData] = useState<
        ProjectAnalytics[] | null
    >(null)
    const [serviceLevelLoading, setServiceLevelLoading] = useState(false)

    const handleViewModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        newViewMode: 'serviceOrders' | 'profit' | null,
    ) => {
        if (newViewMode === null) return
        setViewMode(newViewMode)
    }

    const openSortMenu = (
        event: React.MouseEvent<HTMLElement>,
        header: TableHeader,
    ) => {
        event.stopPropagation()
        setSortMenuAnchor(event.currentTarget)
        setSortMenuHeader(header)
    }

    const closeSortMenu = () => {
        setSortMenuAnchor(null)
        setSortMenuHeader(null)
    }

    const applySort = (sortKey: string) => {
        setOrderBy(sortKey)
        closeSortMenu()
    }

    const sortMenuLabels =
        sortMenuHeader?.sortKind != null
            ? getSortOptionLabels(sortMenuHeader.sortKind)
            : null

    const renderHeaderSortButton = (header: TableHeader) => {
        const activeSortLabel = getActiveSortLabel(header, orderBy)
        const buttonLabel = activeSortLabel ?? 'Sort'
        return (
            <Tooltip
                title={activeSortLabel ?? `Sort by ${header.display}`}
                classes={{ tooltip: classes.tooltip }}
            >
                <Button
                    size="small"
                    fullWidth
                    variant="text"
                    disableRipple
                    className={`${classes.sortButton} ${
                        activeSortLabel ? classes.sortButtonActive : ''
                    }`}
                    aria-label={`Sort ${header.display}`}
                    aria-haspopup="true"
                    aria-expanded={
                        sortMenuOpen && sortMenuHeader?.field === header.field
                    }
                    onClick={(e) => openSortMenu(e, header)}
                >
                    <span className={classes.sortButtonLabel}>
                        {buttonLabel}
                    </span>
                </Button>
            </Tooltip>
        )
    }

    const handleRowClick = async (project: VendorProjectAnalytics) => {
        if (project.id === selectedProject?.id) {
            setSelectedProject(null)
            return
        }
        setServiceLevelLoading(true)
        try {
            const response = await axiosInstance.get(
                `/company/vendor/${vendorId}/service-level-analytics/`,
                {
                    params: { project_id: project.id },
                },
            )
            const results = Array.isArray(response.data)
                ? response.data
                : response.data?.results ?? []
            if (results.length === 0) {
                toast.error('No services!')
            }
            setServiceLevelData(results)
        } catch {
            toast.error('Failed to fetch service level analytics')
        } finally {
            setServiceLevelLoading(false)
        }
        setSelectedProject(project)
    }

    const noopMenuOpen = () => {}

    const renderVendorCell = (
        header: typeof filteredTableHeaders[number],
        project: VendorProjectAnalytics | ProjectAnalytics,
        isServiceLevel = false,
    ) => {
        if (header.field === 'apartment_name') {
            return (
                <TableCell
                    style={{ textAlign: 'left' }}
                    title={project.apartment_name}
                >
                    {project.apartment_name}
                </TableCell>
            )
        }
        if (header.field === 'units_scheduled') {
            return (
                <TableCell style={{ textAlign: 'center' }}>
                    {((project as unknown) as ProjectAnalytics).unique_units ??
                        '-'}
                </TableCell>
            )
        }
        return renderCellContent(
            header,
            (project as unknown) as ProjectAnalytics,
            classes,
            noopMenuOpen,
            theme,
            isServiceLevel,
        )
    }

    const showCodeGate = viewMode === 'profit' && !budgetUnlocked

    const renderChartBody = () => {
        if (viewMode === 'profit' && budgetUnlocked) {
            return (
                <VendorProfitCharts
                    embedded
                    projects={allProjectAnalytics?.data.results ?? []}
                    loading={allProjectsLoading}
                    trends={trends}
                    trendsLoading={trendsLoading}
                    trendsStartDate={trendsStartDate}
                    trendsEndDate={trendsEndDate}
                    onTrendsStartDateChange={setTrendsStartDate}
                    onTrendsEndDateChange={setTrendsEndDate}
                />
            )
        }

        return (
            <ProjectAnalyticsCharts
                embedded
                projects={allProjectsForCharts}
                viewMode="serviceOrders"
                loading={allProjectsLoading}
                hideUnitReadiness
            />
        )
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                height: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Container
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: theme.spacing(1),
                    padding: theme.spacing(1),
                    flexShrink: 0,
                }}
            >
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                    Project Analytics
                </Typography>

                <Container
                    style={{
                        alignItems: 'center',
                        gap: theme.spacing(2),
                        flex: 1,
                        justifyContent: 'center',
                    }}
                >
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                        style={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.grey[300]}`,
                        }}
                    >
                        <ToggleButton
                            value="serviceOrders"
                            aria-label="service orders"
                        >
                            Service Orders
                        </ToggleButton>
                        <ToggleButton value="profit" aria-label="profit">
                            Profit
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Container>

                <Container
                    style={{
                        alignItems: 'center',
                        gap: theme.spacing(1),
                    }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowChartView((prev) => !prev)}
                        startIcon={
                            showChartView ? (
                                <TableChartIcon />
                            ) : (
                                <AssessmentIcon />
                            )
                        }
                        style={{
                            textTransform: 'none',
                            color: showChartView
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                            borderColor: showChartView
                                ? theme.palette.primary.main
                                : theme.palette.grey[400],
                        }}
                    >
                        {showChartView ? 'Table View' : 'Chart View'}
                    </Button>

                    <Typography>Active</Typography>
                    <Switch
                        checked={showArchived}
                        onChange={() => setShowArchived(!showArchived)}
                        className={classes.customSwitch}
                    />
                    <Typography>Archived</Typography>
                </Container>
            </Container>

            {showCodeGate ? (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <VendorInvoiceCodeGate layout="dialog" />
                </Container>
            ) : showChartView ? (
                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {renderChartBody()}
                </div>
            ) : (
                <>
                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflowX: 'auto',
                            overflowY: 'auto',
                        }}
                    >
                        <Table
                            className={classes.table}
                            stickyHeader
                            style={
                                {
                                    '--analytics-title-row-height': `${titleRowHeight}px`,
                                } as React.CSSProperties
                            }
                        >
                            <TableHead className={classes.tableHeader}>
                                <TableRow
                                    ref={titleRowRef}
                                    className={classes.headerTitleRow}
                                >
                                    {filteredTableHeaders.map((header) => (
                                        <Tooltip
                                            key={header.field}
                                            title={header.tooltip}
                                            classes={{
                                                tooltip: classes.tooltip,
                                            }}
                                        >
                                            <TableCell
                                                component="th"
                                                style={{
                                                    width: header.minWidth,
                                                }}
                                            >
                                                {header.display}
                                            </TableCell>
                                        </Tooltip>
                                    ))}
                                </TableRow>
                                <TableRow
                                    className={classes.headerSortToolbarRow}
                                >
                                    {filteredTableHeaders.map((header) => (
                                        <TableCell
                                            key={`sort-${header.field}`}
                                            component="th"
                                            style={{
                                                width: header.minWidth,
                                            }}
                                        >
                                            {header.sortAsc !== null &&
                                                header.sortDesc !== null &&
                                                header.sortKind != null &&
                                                renderHeaderSortButton(header)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            {projectAnalyticsLoading ? (
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            colSpan={
                                                filteredTableHeaders.length
                                            }
                                        >
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {projectAnalytics?.data.results.map(
                                        (project) => (
                                            <React.Fragment key={project.id}>
                                                <TableRow
                                                    onClick={() =>
                                                        handleRowClick(project)
                                                    }
                                                    className={classes.row}
                                                >
                                                    {filteredTableHeaders.map(
                                                        (header) => (
                                                            <React.Fragment
                                                                key={
                                                                    header.field
                                                                }
                                                            >
                                                                {renderVendorCell(
                                                                    header,
                                                                    project,
                                                                )}
                                                            </React.Fragment>
                                                        ),
                                                    )}
                                                </TableRow>
                                                {selectedProject &&
                                                    selectedProject.id ===
                                                        project.id &&
                                                    !serviceLevelLoading &&
                                                    serviceLevelData &&
                                                    serviceLevelData.map(
                                                        (service) => (
                                                            <TableRow
                                                                key={service.id}
                                                                style={{
                                                                    backgroundColor:
                                                                        theme
                                                                            .palette
                                                                            .grey[200],
                                                                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                                                                }}
                                                            >
                                                                {filteredTableHeaders.map(
                                                                    (
                                                                        header,
                                                                    ) => (
                                                                        <React.Fragment
                                                                            key={
                                                                                header.field
                                                                            }
                                                                        >
                                                                            {renderVendorCell(
                                                                                header,
                                                                                service,
                                                                                true,
                                                                            )}
                                                                        </React.Fragment>
                                                                    ),
                                                                )}
                                                            </TableRow>
                                                        ),
                                                    )}
                                            </React.Fragment>
                                        ),
                                    )}
                                </TableBody>
                            )}
                        </Table>

                        <Menu
                            anchorEl={sortMenuAnchor}
                            open={sortMenuOpen}
                            onClose={closeSortMenu}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                        >
                            {sortMenuHeader &&
                                sortMenuLabels &&
                                sortMenuHeader.sortDesc &&
                                sortMenuHeader.sortAsc && (
                                    <>
                                        <MenuItem
                                            selected={
                                                orderBy ===
                                                sortMenuHeader.sortDesc
                                            }
                                            onClick={() =>
                                                applySort(
                                                    sortMenuHeader.sortDesc!,
                                                )
                                            }
                                        >
                                            {orderBy ===
                                                sortMenuHeader.sortDesc && (
                                                <CheckIcon
                                                    fontSize="small"
                                                    style={{ marginRight: 8 }}
                                                />
                                            )}
                                            {sortMenuLabels.high}
                                        </MenuItem>
                                        <MenuItem
                                            selected={
                                                orderBy ===
                                                sortMenuHeader.sortAsc
                                            }
                                            onClick={() =>
                                                applySort(
                                                    sortMenuHeader.sortAsc!,
                                                )
                                            }
                                        >
                                            {orderBy ===
                                                sortMenuHeader.sortAsc && (
                                                <CheckIcon
                                                    fontSize="small"
                                                    style={{ marginRight: 8 }}
                                                />
                                            )}
                                            {sortMenuLabels.low}
                                        </MenuItem>
                                    </>
                                )}
                        </Menu>
                    </div>

                    {/* Pagination Controls */}
                    <div
                        style={{
                            display: 'flex',
                            borderTop: `1px solid ${theme.palette.grey[400]}`,
                            padding: theme.spacing(1),
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            height: 56,
                            flexShrink: 0,
                            backgroundColor: theme.palette.background.paper,
                            fontSize: theme.typography.subtitle1.fontSize,
                            fontFamily: theme.typography.fontFamily,
                        }}
                    >
                        <Container
                            style={{
                                alignItems: 'center',
                                gap: theme.spacing(1),
                            }}
                        >
                            <span>Rows per page:</span>
                            <FormControl size="small" style={{ minWidth: 80 }}>
                                <Select
                                    value={pageSize}
                                    onChange={(e) =>
                                        changePageSize(e.target.value as number)
                                    }
                                    displayEmpty
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                            </FormControl>
                        </Container>

                        <Container
                            style={{
                                alignItems: 'center',
                                gap: theme.spacing(2),
                            }}
                        >
                            <span>
                                {start} - {end} of {totalCount}
                            </span>
                            <Pagination
                                count={numPages}
                                page={currentPage}
                                onChange={(_: unknown, newPage: number) => {
                                    goToPage(newPage)
                                }}
                                size="small"
                            />
                        </Container>
                    </div>
                </>
            )}
        </div>
    )
}
